import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { X, Loader2, Plus, Copy, MessageSquare, ExternalLink, Receipt } from 'lucide-react';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p || 0);
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const lineTotal = (i) => ((parseFloat(i.price) || 0) * (parseInt(i.quantity) || 1));

const Input = ({ label, value, onChange, type = 'text', placeholder = '', testId }) => (
  <div>
    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
    <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 placeholder:text-zinc-700"
           style={FONT_B} data-testid={testId} />
  </div>
);

export const InvoiceBuilder = ({ proposalId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(!!proposalId);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [client, setClient] = useState({ client_name: '', client_phone: '', client_email: '', event_date: '', event_time: '', event_type: '', event_location: '' });
  const [items, setItems] = useState([{ name: '', price: '', quantity: 1 }]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [depositPct, setDepositPct] = useState(50);

  const loadLead = useCallback(async () => {
    try {
      const d = await api.get(`/api/admin/leads/${proposalId}`);
      const p = d.proposal;
      setClient({
        client_name: p.client_name || '', client_phone: p.client_phone || '', client_email: p.client_email || '',
        event_date: p.event_date || '', event_time: p.event_time || '', event_type: p.event_type || '', event_location: p.event_location || '',
      });
      setItems((p.items || []).length > 0
        ? p.items.map(i => ({ name: i.name || '', price: (i.price || 0) + (i.additionalHourPrice || 0) * ((i.hours || 1) - 1), quantity: i.quantity || 1 }))
        : [{ name: '', price: '', quantity: 1 }]);
      setDeliveryFee(p.delivery_fee || 0);
      setDepositPct(p.payment_percent || 50);
    } catch (e) { toast.error('Failed to load lead'); }
    setLoading(false);
  }, [proposalId]);

  useEffect(() => { if (proposalId) loadLead(); }, [proposalId, loadLead]);

  const total = items.reduce((s, i) => s + lineTotal(i), 0) + (parseFloat(deliveryFee) || 0);
  const deposit = Math.round(total * depositPct) / 100;

  const setItem = (idx, field, val) => setItems(arr => { const n = [...arr]; n[idx] = { ...n[idx], [field]: val }; return n; });

  const save = async () => {
    if (!proposalId && !client.client_name.trim()) { toast.error('Client name is required'); return; }
    if (items.filter(i => i.name.trim()).length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/admin/invoices', {
        proposal_id: proposalId || null,
        ...client,
        items: items.filter(i => i.name.trim()).map(i => ({ name: i.name.trim(), price: parseFloat(i.price) || 0, quantity: parseInt(i.quantity) || 1 })),
        delivery_fee: parseFloat(deliveryFee) || 0,
        deposit_percent: depositPct,
      });
      setSaved(res);
      toast.success(`Invoice ${res.invoice_number} saved`);
    } catch (e) { toast.error(e.message || 'Failed to save invoice'); }
    setSaving(false);
  };

  const shareLink = saved ? `${window.location.origin}${saved.invoice_path}` : '';
  const smsBody = saved ? `Hi ${(client.client_name || '').split(' ')[0]}! Your Exotic Animals Miami invoice ${saved.invoice_number} is ready — total ${fmt(saved.total)}, deposit ${fmt(saved.amount_due)} to lock your date. View & pay securely: ${shareLink}` : '';

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="invoice-builder">
      <div className="max-w-lg mx-auto min-h-screen bg-[#050505] border-x border-white/5 px-6 py-6">
        <div className="flex items-start justify-between mb-7">
          <div>
            <h2 className="text-white text-[26px] font-black tracking-tighter flex items-center gap-3" style={FONT_H}>
              <Receipt className="w-5 h-5 text-zinc-400" /> {proposalId ? 'Edit Invoice' : 'New Invoice'}
            </h2>
            <p className="text-zinc-500 text-xs mt-1.5" style={FONT_B}>{proposalId ? client.client_name : 'Build an invoice from scratch and text it to anyone.'}</p>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 text-zinc-300 active:scale-95 transition-transform" data-testid="invoice-builder-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading…</p> : saved ? (
          <div data-testid="invoice-share">
            <div className="bg-[#111111] border border-white/5 rounded-2xl p-6 text-center mb-6">
              <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-semibold" style={FONT_B}>Invoice {saved.invoice_number}</p>
              <p className="text-white text-[40px] font-black tracking-tighter tabular-nums mt-1" style={FONT_H}>{fmt(saved.total)}</p>
              <p className="text-zinc-500 text-xs" style={FONT_B}>deposit {fmt(saved.amount_due)} ({depositPct}%)</p>
            </div>
            <div className="space-y-3">
              <a href={shareLink} target="_blank" rel="noopener noreferrer"
                 className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-black text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="invoice-open-link">
                <ExternalLink className="w-4 h-4" /> Open Invoice
              </a>
              {client.client_phone && (
                <a href={`sms:${client.client_phone}?&body=${encodeURIComponent(smsBody)}`}
                   className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/5 border border-white/10 text-white text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="invoice-text-link">
                  <MessageSquare className="w-4 h-4" /> Text to Client
                </a>
              )}
              <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Link copied'); }}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="invoice-copy-link">
                <Copy className="w-4 h-4" /> Copy Link
              </button>
              <button onClick={() => { onSaved && onSaved(saved.proposal_id); }}
                      className="w-full px-6 py-3.5 text-zinc-500 text-sm font-bold" style={FONT_B} data-testid="invoice-done">Done</button>
            </div>
          </div>
        ) : (
          <>
            {!proposalId && (
              <div className="space-y-4 mb-7">
                <Input label="Client Name *" value={client.client_name} onChange={(v) => setClient(c => ({ ...c, client_name: v }))} placeholder="Who is this for?" testId="invoice-client-name" />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Phone" value={client.client_phone} onChange={(v) => setClient(c => ({ ...c, client_phone: v }))} type="tel" testId="invoice-client-phone" />
                  <Input label="Email" value={client.client_email} onChange={(v) => setClient(c => ({ ...c, client_email: v }))} type="email" testId="invoice-client-email" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Event Date" value={client.event_date} onChange={(v) => setClient(c => ({ ...c, event_date: v }))} type="date" testId="invoice-event-date" />
                  <Input label="Event Type" value={client.event_type} onChange={(v) => setClient(c => ({ ...c, event_type: v }))} placeholder="Birthday…" testId="invoice-event-type" />
                </div>
              </div>
            )}

            <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Line Items</p>
            <div className="space-y-3 mb-4">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input value={it.name} placeholder="Item…" onChange={(e) => setItem(idx, 'name', e.target.value)}
                         className="flex-1 min-w-0 bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 placeholder:text-zinc-700" style={FONT_B} data-testid={`invoice-item-name-${idx}`} />
                  <input type="number" value={it.quantity} min="1" onChange={(e) => setItem(idx, 'quantity', e.target.value)}
                         className="w-14 bg-white/[0.04] border border-white/10 text-white text-sm px-2 py-3 rounded-xl text-center tabular-nums focus:outline-none focus:border-white/30" data-testid={`invoice-item-qty-${idx}`} />
                  <input type="number" value={it.price} placeholder="$" onChange={(e) => setItem(idx, 'price', e.target.value)}
                         className="w-24 bg-white/[0.04] border border-white/10 text-white text-sm px-3 py-3 rounded-xl text-right tabular-nums focus:outline-none focus:border-white/30 placeholder:text-zinc-700" data-testid={`invoice-item-price-${idx}`} />
                  <button onClick={() => setItems(arr => arr.filter((_, i) => i !== idx))}
                          className="w-9 h-9 flex items-center justify-center rounded-full text-zinc-600 active:scale-95 flex-shrink-0 transition-transform" data-testid={`invoice-item-remove-${idx}`} aria-label="Remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setItems(arr => [...arr, { name: '', price: '', quantity: 1 }])}
                    className="flex items-center gap-1.5 text-white text-xs font-bold mb-6" style={FONT_B} data-testid="invoice-add-item">
              <Plus className="w-3.5 h-3.5" /> Add Item
            </button>

            <div className="flex items-center justify-between mb-6">
              <span className="text-zinc-500 text-[11px] uppercase tracking-[0.15em] font-semibold" style={FONT_B}>Delivery Fee</span>
              <input type="number" value={deliveryFee} onChange={(e) => setDeliveryFee(e.target.value)}
                     className="w-24 bg-white/[0.04] border border-white/10 text-white text-sm px-3 py-3 rounded-xl text-right tabular-nums focus:outline-none focus:border-white/30" data-testid="invoice-delivery-fee" />
            </div>

            <p className="text-zinc-500 text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={FONT_B}>Deposit Required</p>
            <div className="flex gap-2 mb-7" data-testid="invoice-deposit-options">
              {[25, 50, 100].map(pct => (
                <button key={pct} onClick={() => setDepositPct(pct)}
                        className={`px-5 py-2.5 rounded-full text-[13px] font-bold transition-colors ${depositPct === pct ? 'bg-white text-black' : 'bg-white/5 text-zinc-500 border border-white/10'}`}
                        style={FONT_B} data-testid={`invoice-deposit-${pct}`}>
                  {pct === 100 ? 'Full' : `${pct}%`}
                </button>
              ))}
            </div>

            <div className="bg-[#111111] border border-white/5 rounded-2xl p-5 mb-7">
              <div className="flex justify-between text-sm mb-2" style={FONT_B}>
                <span className="text-zinc-500">Total</span>
                <span className="text-white font-black text-lg tabular-nums" style={FONT_H} data-testid="invoice-total">{fmt(total)}</span>
              </div>
              <div className="flex justify-between text-sm" style={FONT_B}>
                <span className="text-zinc-500">Deposit due now</span>
                <span className="text-zinc-300 font-bold tabular-nums" data-testid="invoice-deposit-amount">{fmt(deposit)}</span>
              </div>
            </div>

            <div className="flex gap-3 pb-10">
              <button onClick={save} disabled={saving}
                      className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="invoice-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save Invoice
              </button>
              <button onClick={onClose} className="px-7 py-3.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="invoice-cancel">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
